import React from 'react';
import PropTypes from 'prop-types';

import { useContext } from '@hooks';
import { mergeClasses } from '@utils';

import { ProductCardContext } from './ProductCard';

/**
 * ProductCard.Title is a child component that displays the product title within a ProductCard component.
 * Must be used as a child component of the ProductCard.
 *
 * @component ProductCard.Title
 * @param {Object} props - The properties for the ProductCard.Title component.
 * @param {React.ReactNode} props.children - The content of the product title.
 * @param {string} [props.className] - Optional additional CSS classes to apply to the ProductCard.Title container.
 * @param {React.ElementType} [props.as] - The HTML tag or React component to render as the title wrapper element.
 * @returns {ReactElement} The rendered ProductCard.Title component.
 * @example
 * <ProductCard
 *   href="#"
 *   productId={266712}
 *   isExclusive={true}
 *   isLimited={false}
 *   isPreorder={false}
 * >
 *   <ProductCard.Image src="https://thumblr.uniid.it/product/266
 *   <ProductCard.Title>Nike Air Zoom Mercurial Superfly 9 Academy AG Dream Speed 6</ProductCard.Title>
 * </ProductCard>
 */
export default function ProductCardTitle({
  children,
  className,
  as: Component,
  ...restProps
}) {
  useContext(
    ProductCardContext,
    'ProductCard.Title must be a child of ProductCard.'
  );

  return (
    <Component
      className={mergeClasses(
        'm-0 line-clamp-3 min-h-[52.5px] p-0 px-2 text-sm font-bold leading-tight text-black @md:px-4',
        className
      )}
      {...restProps}
    >
      {children}
    </Component>
  );
}

ProductCardTitle.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  as: PropTypes.elementType
};

ProductCardTitle.defaultProps = {
  className: '',
  as: 'h3'
};
